import {
  cloneDeep,
  flatMap,
  flatten,
  take,
  takeWhile,
  toInteger,
  toNumber,
} from 'lodash';
import {
  CreditPricing,
  CreditPricingLeaseTerm,
  CreditTier,
  CreditPricingLeasePricing,
} from '@upstart/harmony/lib/legos/creditPricingType';
import {
  FinanceType,
  ORDERED_LENDERS,
} from '@upstart/harmony/lib/legos/constants';
import {
  convertNewCreditPricingToOld,
  transformToRangeTierType,
} from '@upstart/harmony/lib/credit-pricing-utils';
import { AprConfig } from '@upstart/harmony/lib/legos/dealerTypes';
import {
  LeasePricingTerm,
  LeasePricingTierTermValue,
  PurchasePricingValues,
  Tier,
  TieredAprConfig,
  LeasePricingOptions,
  LeasePricingValue,
  TieredAprConfigOptions,
  TierKey,
  UseManufacturerAprValues,
  ThirdPartyLenders,
} from './types';
import {
  ORDERED_TIERS,
  TIER_KEY_TO_LABEL,
  MAX_LEASE_TERMS,
  MAX_LEASE_MILEAGES,
} from './constants';

/**
 * Groups month, mileage into single array
 * @example
 *  { months: 36, mileage: 12000 },
 *  { months: 36, mileage: 15000 },
 *  { months: 36, mileage: 18000 }, --------> [ { months: '36', mileages: [12000,15000,18000]}]
 *  { months: 42, mileage: 12000 }, --------> [ { months: '42', mileages: [12000,15000,18000]}]
 *  { months: 42, mileage: 15000 },
 *  { months: 42, mileage: 18000 },
 * @param terms
 */
export const transformPricingTermsLease = (
  terms: CreditPricingLeaseTerm[],
): LeasePricingTerm[] => {
  if (!terms) return [];
  const leaseMap = new Map<number, number[]>();
  terms.forEach(({ months, mileage }) => {
    if (!leaseMap.has(months)) leaseMap.set(months, []);
    leaseMap?.get(months)?.push(mileage);
  });
  return padArrayUntil(
    Array.from(leaseMap.entries()).map(
      ([key, value]): LeasePricingTerm => ({
        months: key?.toString() || '',
        mileages: padArrayUntil(
          convertArray(value, (v) => v?.toString() || ''),
          '',
          (x) => x.length < MAX_LEASE_MILEAGES,
        ),
      }),
    ),
    {
      months: '',
      mileages: ['', '', '', '', ''],
    },
    (x) => x.length < MAX_LEASE_TERMS,
  );
};

/**
 * Compress pricing matrix for API friendly type
 * @param param0
 */
export const compressPricingMatrix = ({
  terms,
  pricing,
}: LeasePricingOptions): CreditPricingLeasePricing[][] => {
  if (!terms || terms.length === 0) return [];
  if (!pricing || pricing.length === 0) return [];
  // for now mileages stay constant across all lease terms
  const mileages = sliceBeforeEqualTo(terms[0].mileages, (x) => !x);
  return pricing.map((tierValues) =>
    flatten(
      sliceBeforeEqualTo(terms, ({ months }) => !months).map((_, index) => {
        const start = index * MAX_LEASE_MILEAGES;
        const end = start + mileages.length;
        return tierValues.slice(start, end).map(
          ({
            onePayLeaseMoneyFactor,
            moneyFactor,
          }): CreditPricingLeasePricing => ({
            onePayLeaseMoneyFactor: toInteger(onePayLeaseMoneyFactor),
            moneyFactor: toInteger(moneyFactor),
          }),
        );
      }),
    ),
  );
};

/**
 * Uncompress pricing matrix to make it easier to do UI updates on front-end
 * @param creditPricing
 */
export const uncompressPricingMatrix = ({
  terms,
  pricing,
}: {
  terms: CreditPricingLeaseTerm[];
  pricing: CreditPricingLeasePricing[][];
}): LeasePricingTierTermValue[][] => {
  const leaseTerms = transformPricingTermsLease(terms);
  return pricing.map((tierValues) => {
    const clone = [...tierValues];
    return flatMap(
      leaseTerms,
      ({ mileages }: { mileages: LeasePricingValue[] }) => {
        const slicedMileages = sliceBeforeEqualTo(mileages, (x) => !x);
        return padArrayUntil(
          clone.splice(0, slicedMileages.length).map(
            ({
              moneyFactor,
              onePayLeaseMoneyFactor,
            }): LeasePricingTierTermValue => ({
              moneyFactor: moneyFactor?.toString() || '',
              onePayLeaseMoneyFactor: onePayLeaseMoneyFactor?.toString() || '',
            }),
          ),
          { onePayLeaseMoneyFactor: '', moneyFactor: '' },
          (x) => x.length < MAX_LEASE_MILEAGES,
        );
      },
    );
  });
};

export const buildTiersForOverride = (
  options: TieredAprConfigOptions,
  purchaseCreditTiers: Tier[],
  field: 'useManufacturerApr' | 'overrideTierForThirdPartyLenders',
): UseManufacturerAprValues => {
  purchaseCreditTiers.forEach((tier: Tier, index: number) => {
    const tierKey = (tier.key as TierKey) || `tier${index + 1}`;
    const obj = options[field];
    if (!obj) {
      return;
    }
    if (!(tierKey in obj)) {
      obj[tierKey] = false;
    }
  });
  return options[field] as UseManufacturerAprValues;
};

export const buildThirdPartyLenders = (
  thirdPartyLenders: ThirdPartyLenders,
) => {
  const sanitizedThirdPartyLenders: ThirdPartyLenders = {};
  ORDERED_LENDERS.forEach((lender) => {
    sanitizedThirdPartyLenders[lender] = thirdPartyLenders?.[lender] || false;
  });

  return sanitizedThirdPartyLenders;
};

// Convert from api friendly type to ui compatible type
export const transformToAprConfig = (
  creditPricing: CreditPricing,
): TieredAprConfig => ({
  enabled: true,
  tiers: {
    [FinanceType.PURCHASE]: transformToRangeTierType(
      creditPricing.creditPricing.purchase.creditTiers,
    ),
    [FinanceType.LEASE]: transformToRangeTierType(
      creditPricing.creditPricing.lease.creditTiers,
    ),
  },
  options: {
    useManufacturerApr: Object.entries(
      creditPricing?.options?.useManufacturerApr || {},
    ).reduce(
      (accum, [key, value]) => ({ ...accum, [key]: value }),
      {} as { [index: string]: boolean },
    ),
    useManufacturerRebate: {
      ...creditPricing.options.useManufacturerRebate,
    },
    addOEMMarkupRate: creditPricing.options.enableMarkup,
    enableManufacturerApr:
      creditPricing.options.enableManufacturerApr ||
      Object.values(creditPricing?.options?.useManufacturerApr || {}).some(
        (x) => x,
      ),
    enableThirdPartyLenders:
      creditPricing.options.enableThirdPartyLenders || false,
    thirdPartyLenders: creditPricing.options.thirdPartyLenders,
    overrideTierForThirdPartyLenders:
      creditPricing.options.overrideTierForThirdPartyLenders,
  },
  pricing: {
    [FinanceType.LEASE]: {
      terms: transformPricingTermsLease(
        creditPricing.creditPricing.lease.terms,
      ),
      pricing: uncompressPricingMatrix(
        creditPricing.creditPricing[FinanceType.LEASE],
      ),
    },
    [FinanceType.PURCHASE]: {
      terms: creditPricing.creditPricing.purchase.terms.map((t) =>
        t.months.toString(),
      ),
      pricing: creditPricing.creditPricing.purchase.pricing.map((value) =>
        value.map(
          (v): PurchasePricingValues => ({
            apr: v.apr.toString(),
            markup: v.markup.toString(),
          }),
        ),
      ),
    },
  },
});

// Convert to a friendly api compatible type
export const transformToCreditPricing = (
  aprConfig: TieredAprConfig,
): CreditPricing => ({
  creditPricing: {
    [FinanceType.PURCHASE]: {
      creditTiers: transformToSingleTierType(
        aprConfig?.tiers?.[FinanceType.PURCHASE] || [],
      ),
      terms: aprConfig.pricing.purchase.terms.map((t) => ({
        months: toInteger(t),
      })),
      pricing: aprConfig.pricing.purchase.pricing.map((tiers) =>
        tiers.map((terms) => ({
          apr: toNumber(terms.apr),
          markup: aprConfig.options.addOEMMarkupRate
            ? toNumber(terms.markup)
            : 0,
        })),
      ),
    },
    [FinanceType.LEASE]: {
      creditTiers: transformToSingleTierType(
        aprConfig?.tiers?.[FinanceType.LEASE] || [],
      ),
      // for now, we will keep mileages constant across all terms by replication mileage of first term
      terms: aprConfig.pricing.lease.terms
        ? flatten(
            sliceBeforeEqualTo(
              aprConfig.pricing.lease.terms,
              ({ months }) => !months,
            ).map(({ months }) =>
              sliceBeforeEqualTo(
                aprConfig.pricing.lease.terms[0].mileages,
                (m) => !m,
              ).map((m) => ({
                months: toInteger(months),
                mileage: toInteger(m),
              })),
            ),
          )
        : [],
      pricing: compressPricingMatrix(aprConfig.pricing[FinanceType.LEASE]),
    },
  },
  options: {
    enableMarkup: !!aprConfig.options.addOEMMarkupRate,
    enableManufacturerApr: aprConfig.options.enableManufacturerApr,
    enableThirdPartyLenders: aprConfig.options.enableThirdPartyLenders,
    thirdPartyLenders: buildThirdPartyLenders(
      aprConfig.options.thirdPartyLenders || {},
    ),
    overrideTierForThirdPartyLenders: buildTiersForOverride(
      aprConfig.options,
      aprConfig?.tiers?.[FinanceType.PURCHASE] ?? [],
      'overrideTierForThirdPartyLenders',
    ) as { [index in TierKey]: boolean },
    useManufacturerApr: buildTiersForOverride(
      aprConfig.options,
      aprConfig?.tiers?.[FinanceType.PURCHASE] ?? [],
      'useManufacturerApr',
    ) as { [index in TierKey]: boolean },
    useManufacturerRebate: [
      FinanceType.CASH,
      FinanceType.LEASE,
      FinanceType.PURCHASE,
    ].reduce(
      (accum, financeType) => ({
        ...accum,
        [financeType]:
          aprConfig?.options?.useManufacturerRebate?.[financeType] ?? false,
      }),
      {
        [FinanceType.CASH]: false,
        [FinanceType.LEASE]: false,
        [FinanceType.PURCHASE]: false,
      } as { [index in FinanceType]: boolean },
    ),
  },
});

export const transformAndConvert = (aprConfig: TieredAprConfig): AprConfig => {
  const creditPricing = transformToCreditPricing(aprConfig);
  return convertNewCreditPricingToOld(creditPricing);
};

/*
    Functions to transform UI friendly types to API friendly type
*/

/**
 * Converts single value tier type to a low/high range tier type
 * @example
 * [751, 850]        { name: 'Tier1', key: 'tier1', value: 751, displayName: '' },
 * [651, 750] -----> { name: 'Tier2', key: 'tier2', value: 651, displayName: ''  },
 * [601, 650] -----> { name: 'Tier3', key: 'tier3', value: 601, displayName: ''  },
 * [500, 600]        { name: 'Tier4', key: 'tier4', value: 500, displayName: ''  },
 */
export const transformToSingleTierType = (values: Tier[]): CreditTier[] =>
  take(ORDERED_TIERS, Math.min(ORDERED_TIERS.length, values.length)).map(
    (key, index) => ({
      name: TIER_KEY_TO_LABEL[key],
      key,
      value: toInteger(values[index].low),
      isDefault: values[index].isDefault || false,
      displayName: values[index].displayName || '',
    }),
  );

const convertArray = <T, K>(items: T[], predicate: (value: T) => K) =>
  items.map(predicate);

const padArrayUntil = <T>(
  items: T[],
  filler: T,
  predicate: (values: T[]) => boolean,
): T[] => {
  const result = [...items];
  while (predicate(result)) result.push(cloneDeep(filler));
  return result;
};

/**
 * Returns all items in array from zero index until predicate condition is evaluated to false
 * @param items items in array
 * @param predicate predicate boolean func
 * @param start optional start index
 */
export const sliceBeforeEqualTo = <T>(
  items: T[],
  predicate: (value: T) => boolean,
): T[] => takeWhile(items, (value) => !predicate(value));
